import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Edit, Upload, FileText } from 'lucide-react';
import { getErrorMessage } from '@/lib/utils';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All major Indian cities
const ALL_CITIES = [
  'Agra', 'Ahmedabad', 'Ajmer', 'Aligarh', 'Allahabad', 'Amritsar', 'Aurangabad',
  'Bangalore', 'Bareilly', 'Belgaum', 'Bhavnagar', 'Bhilai', 'Bhopal', 'Bhubaneswar', 'Bikaner',
  'Chandigarh', 'Chennai', 'Coimbatore', 'Cuttack',
  'Dehradun', 'Delhi', 'Dhanbad', 'Durgapur',
  'Erode',
  'Faridabad', 'Firozabad',
  'Ghaziabad', 'Gorakhpur', 'Guntur', 'Gurgaon', 'Guwahati', 'Gwalior',
  'Hubli', 'Hyderabad',
  'Indore',
  'Jabalpur', 'Jaipur', 'Jalandhar', 'Jammu', 'Jamnagar', 'Jamshedpur', 'Jhansi', 'Jodhpur',
  'Kanpur', 'Kochi', 'Kolhapur', 'Kolkata', 'Kota', 'Kozhikode',
  'Lucknow', 'Ludhiana',
  'Madurai', 'Mangalore', 'Mathura', 'Meerut', 'Moradabad', 'Mumbai', 'Mysore',
  'Nagpur', 'Nanded', 'Nashik', 'Navi Mumbai', 'Noida',
  'Patna', 'Pimpri-Chinchwad', 'Pondicherry', 'Pune',
  'Raipur', 'Rajkot', 'Ranchi',
  'Salem', 'Sangli', 'Siliguri', 'Solapur', 'Srinagar', 'Surat',
  'Thane', 'Thiruvananthapuram', 'Tiruchirappalli', 'Tiruppur',
  'Udaipur', 'Ujjain',
  'Vadodara', 'Varanasi', 'Vasai-Virar', 'Vijayawada', 'Visakhapatnam',
  'Warangal'
];

export default function EditProfile() {
  const { user, token, login } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    company_name: user?.company_name || '',
    division: user?.division || '',
    city: user?.city || '',
    mr_visiting_card: user?.mr_visiting_card || '',
    mr_photo: user?.mr_photo || '',
  });

  const handleVisitingCardChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, mr_visiting_card: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, mr_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.patch(
        `${API}/auth/me`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      login(token, response.data);
      toast.success('Profile updated successfully!');
      setIsOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        data-testid="edit-profile-btn"
        className="rounded-full bg-primary hover:bg-primary-hover text-sm px-4"
      >
        <Edit className="mr-1 sm:mr-2 h-4 w-4" />
        Edit
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg">Edit Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Photo Upload */}
            <div>
              <Label>Your Photo</Label>
              <div className="mt-1.5">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  id="mr-photo-upload"
                />
                <label
                  htmlFor="mr-photo-upload"
                  className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                >
                  {formData.mr_photo ? (
                    <div className="flex items-center gap-3">
                      <img 
                        src={formData.mr_photo} 
                        alt="Profile" 
                        className="h-16 w-16 rounded-full object-cover"
                      />
                      <div className="text-center">
                        <p className="text-sm text-primary font-medium">Photo uploaded</p>
                        <p className="text-xs text-slate-400">Click to change</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="mx-auto h-6 w-6 text-slate-400" />
                      <p className="mt-1 text-sm text-slate-600">Upload photo</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div>
              <Label>Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>Company Name</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>Division</Label>
              <Input
                value={formData.division}
                onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>City</Label>
              <Select
                value={formData.city}
                onValueChange={(value) => setFormData({ ...formData, city: value })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select your city" />
                </SelectTrigger>
                <SelectContent className="max-h-[250px]">
                  {ALL_CITIES.map(city => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Visiting Card</Label>
              <div className="mt-1.5">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleVisitingCardChange}
                  className="hidden"
                  id="visiting-card-upload"
                />
                <label
                  htmlFor="visiting-card-upload"
                  className="flex items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                >
                  {formData.mr_visiting_card ? (
                    <div className="text-center p-4">
                      <FileText className="mx-auto h-10 w-10 text-primary" />
                      <p className="mt-2 text-sm text-primary font-medium">Visiting card uploaded</p>
                      <p className="text-xs text-slate-400">Click to replace</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="mx-auto h-6 w-6 text-slate-400" />
                      <p className="mt-1 text-sm text-slate-600">Upload visiting card</p>
                      <p className="text-xs text-slate-400">Image or PDF up to 10MB</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 rounded-full bg-primary hover:bg-primary-hover"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
